import { useState } from 'react';
import { Trash2 } from 'lucide-react';
import { useCreateLabel, useDeleteLabel, useLabels, useUpdateLabel } from '../../hooks/useLabels';
import { LabelPill } from './LabelPill';
import { useConfirm } from '../ui/ConfirmProvider';

interface LabelsManagerProps {
  onClose: () => void;
}

const DEFAULT_COLOR = '#6366f1';

/** Modal to create, recolor, rename, and delete task labels. */
export function LabelsManager({ onClose }: LabelsManagerProps) {
  const { data: labels } = useLabels();
  const createLabel = useCreateLabel();
  const updateLabel = useUpdateLabel();
  const deleteLabel = useDeleteLabel();
  const confirm = useConfirm();

  const [name, setName] = useState('');
  const [color, setColor] = useState(DEFAULT_COLOR);

  function create() {
    const trimmed = name.trim();
    if (!trimmed) return;
    createLabel.mutate(
      { name: trimmed, color },
      {
        onSuccess: () => {
          setName('');
          setColor(DEFAULT_COLOR);
        },
      },
    );
  }

  return (
    <div className="ui-modal-backdrop" onClick={onClose}>
      <div className="ui-modal-panel w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">Gestionează etichete</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
            aria-label="Închide"
          >
            ✕
          </button>
        </div>

        <div className="mt-4 flex items-end gap-2">
          <input
            type="color"
            value={color}
            onChange={(e) => setColor(e.target.value)}
            className="h-9 w-9 shrink-0 cursor-pointer rounded border border-gray-300"
            aria-label="Culoare etichetă"
          />
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && create()}
            placeholder="Nume etichetă nouă…"
            className="ui-input min-w-0 flex-1 text-sm"
          />
          <button
            type="button"
            onClick={create}
            disabled={!name.trim() || createLabel.isPending}
            className="ui-btn-primary"
          >
            Adaugă
          </button>
        </div>

        <ul className="mt-4 space-y-2">
          {(labels ?? []).map((l) => (
            <li key={l.id} className="flex items-center gap-2">
              <input
                type="color"
                value={l.color}
                onChange={(e) => updateLabel.mutate({ id: l.id, color: e.target.value })}
                className="h-7 w-7 shrink-0 cursor-pointer rounded border border-gray-300"
                aria-label={`Culoare pentru ${l.name}`}
              />
              <input
                defaultValue={l.name}
                onBlur={(e) => {
                  const v = e.target.value.trim();
                  if (v && v !== l.name) updateLabel.mutate({ id: l.id, name: v });
                }}
                className="ui-input min-w-0 flex-1 text-sm"
              />
              {/* The preview repeats the name and colour right next to both; on
                  a phone it only squeezed the name field down to a few letters. */}
              <span className="hidden sm:inline-flex">
                <LabelPill label={l} />
              </span>
              <span className="w-10 text-right text-xs text-gray-400">{l.taskCount}</span>
              <button
                type="button"
                onClick={async () => {
                  if (await confirm({ message: `Ștergi eticheta "${l.name}"?`, danger: true }))
                    deleteLabel.mutate(l.id);
                }}
                className="text-gray-300 hover:text-red-600"
                aria-label={`Șterge ${l.name}`}
              >
                <Trash2 size={15} />
              </button>
            </li>
          ))}
          {(labels ?? []).length === 0 && (
            <li className="text-sm text-gray-400">Încă nu există etichete.</li>
          )}
        </ul>
      </div>
    </div>
  );
}
